/**
 * Generic monthly stacked-bar chart: one or more `stackId` groups (e.g. an
 * "inflow" group and an "outflow" group), each series rendered as its own
 * `<Bar>`. Every value is a non-negative magnitude -- inflow and outflow
 * render as two same-direction bars side by side per month (Recharts
 * automatically offsets distinct `stackId` groups within one category), the
 * difference between them readable at a glance without a sign-based
 * above/below split. Direction is conveyed by which group a series belongs
 * to; a sub-split within a group (region, or reconciled/unreconciled) is
 * conveyed by that series' own color, plus a lighter fill for the outflow
 * group so the two groups also read as visually distinct at a glance.
 *
 * Extracted rather than forked from the Property Portfolio chart pattern
 * (components/PortfolioMetricChart.jsx): that component is hardcoded to
 * exactly two regions with fixed GBP/EUR native currencies (ukNative/
 * frNative baked into its data-building and tooltip code), so it can't
 * express a 3-way region split or a reconciliation-state split. This
 * component reuses the same proven sub-patterns instead of reinventing
 * them: per-segment center LabelList values (formatCompact) and a
 * `content`-based stack-total LabelList on each group's outermost bar
 * (Recharts v3 doesn't resolve a stack total via `dataKey` -- see
 * PortfolioMetricChart.jsx / BROSDashboardCashflowTab.jsx), plus the >20
 * -point shouldSkipDataLabels gate.
 */
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { formatCompact, shouldSkipDataLabels } from '../../lib/chartFormatters';

/**
 * @param {object} props
 * @param {string} props.title
 * @param {Array<object>} props.data - [{ month, ...seriesKeys }], every value a non-negative magnitude
 * @param {Array<{key: string, label: string, color: string, stackId: string, opacity?: number}>} props.series
 * @param {string} props.displayCurrency
 * @param {React.ReactNode} [props.footer] - rendered inside the same CardContent, below the chart (e.g. a MonthlyFiguresRow aligned to the month columns).
 */
export function StackedFlowChart({ title, data, series, displayCurrency, footer, 'data-testid': testId }) {
  if (!data || data.length === 0) return null;

  const skipLabels = shouldSkipDataLabels(data);

  // Last-declared series in each stackId group renders the group's
  // stack-total label (the outermost segment of that group, per the
  // Recharts v3 gotcha these charts all work around).
  const lastKeyByStackId = {};
  series.forEach((s) => { lastKeyByStackId[s.stackId] = s.key; });
  const groupKeysByStackId = series.reduce((acc, s) => {
    (acc[s.stackId] = acc[s.stackId] || []).push(s.key);
    return acc;
  }, {});

  // Inflow and outflow series share the same label/color per category (e.g.
  // "France" appears once for FR_in, once for FR_out) -- only the first
  // occurrence of each label gets a Legend entry, so "France" doesn't list
  // twice.
  const seenLegendLabels = new Set();

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              tickFormatter={(v) => formatCompact(v, displayCurrency)}
            />
            <Tooltip
              // Inflow and outflow series share a `name` per category (e.g.
              // both UK_in and UK_out are labeled "UK", so the Legend only
              // lists it once -- see seenLegendLabels below). The Tooltip
              // has no such dedup: left as `name` alone, hovering a month
              // shows "UK" twice, "France" twice, "Unassigned" twice, with
              // different values and no way to tell which is which. Suffix
              // with the series' own direction (derived from its dataKey,
              // not a second lookup) so the two rows read as distinct
              // in/out entries instead of an apparent duplicate.
              formatter={(value, name, props) => {
                const direction = props?.dataKey?.endsWith('_out') ? 'Out' : 'In';
                return [formatCompact(value, displayCurrency), `${name} (${direction})`];
              }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {series.map((s) => {
              const isGroupTotal = lastKeyByStackId[s.stackId] === s.key;
              const groupKeys = groupKeysByStackId[s.stackId];
              const isDuplicateLegendLabel = seenLegendLabels.has(s.label);
              seenLegendLabels.add(s.label);
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  stackId={s.stackId}
                  fill={s.color}
                  fillOpacity={s.opacity ?? 1}
                  legendType={isDuplicateLegendLabel ? 'none' : 'square'}
                >
                  {!skipLabels && (
                    <LabelList
                      dataKey={s.key}
                      position="center"
                      formatter={(v) => (v >= 1000 ? formatCompact(v, displayCurrency) : '')}
                      style={{ fontSize: 10, fontWeight: 600, fill: '#ffffff' }}
                    />
                  )}
                  {!skipLabels && isGroupTotal && (
                    <LabelList
                      content={({ x, y, width, index }) => {
                        const row = data[index];
                        const total = groupKeys.reduce((sum, k) => sum + (row?.[k] || 0), 0);
                        if (!total) return null;
                        // Every group is non-negative and grows upward from
                        // the zero axis, so the total always sits above it.
                        return (
                          <text
                            x={x + width / 2}
                            y={y - 6}
                            textAnchor="middle"
                            fontSize={11}
                            fontWeight={600}
                            fill="hsl(var(--foreground))"
                          >
                            {formatCompact(total, displayCurrency)}
                          </text>
                        );
                      }}
                    />
                  )}
                </Bar>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
        {footer}
      </CardContent>
    </Card>
  );
}
